import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faTerminal,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { personalInfo } from "../data/portfolioData";

export default function About() {
  const practicalHighlights = [
    {
      title: "SIEM & Security Operations",
      desc: "Architecting Wazuh deployments, agent onboarding, and multi-tier log retention policies for dependable forensic observability.",
    },
    {
      title: "Vulnerability Assessment (VAPT)",
      desc: "Structured evaluations of web applications, REST APIs, and authentication flows against OWASP Top 10 vulnerabilities.",
    },
    {
      title: "Host & Infrastructure Hardening",
      desc: "Enforcing CIS-aligned Windows Group Policy (GPO) baselines, registry lockdown, and removing legacy protocol attack vectors.",
    },
    {
      title: "OSINT & Investigation",
      desc: "Systematic external surface reconnaissance, digital footprint mapping, and threat intelligence analysis.",
    },
    {
      title: "Client Assessment & Training",
      desc: "Translating technical audit findings into actionable mitigation plans for stakeholders, combined with hands-on security awareness sessions.",
    },
  ];

  return (
    <section id="about" className="cyber-section">
      <div className="container">
        <div className="section-label">PRACTICAL PHILOSOPHY</div>

        <h2 className="section-title mb-4" style={{ maxWidth: "850px" }}>
          Security is more than finding vulnerabilities. It's about understanding the system.
        </h2>

        <div className="row g-5 align-items-start mt-1">
          {/* Left: Narrative */}
          <div className="col-lg-7">
            <div className="d-flex flex-column gap-3" style={{ fontSize: "1.05rem", color: "var(--text-secondary)", lineHeight: 1.8 }}>
              <p>
                I am a <strong style={{ color: "var(--text-primary)" }}>Security Analyst</strong> based in Kolkata, India.
                My work centers on practical defense: engineering centralized log observability with Wazuh SIEM, conducting methodical web and API penetration testing, and systematically hardening enterprise operating systems against adversary techniques.
              </p>

              <p>
                Rather than treating security as an isolated checklist, I analyze how infrastructure, software architecture, and identity management intersect. From configuring Syslog pipelines and index life cycles to writing custom Wazuh decoders and dissecting complex business-logic flaws, I build resilience directly into the operational stack.
              </p>

              <p>
                In addition to offensive security assessments, I frequently conduct threat intelligence and OSINT investigations, evaluate client risk postures, and deliver cybersecurity training to empower developers and sysadmins to build and configure with security in mind.
              </p>
            </div>

            {/* TryHackMe Badge & Verification */}
            <div className="cyber-card-elevated p-3 mt-4">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: "8px",
                      background: "rgba(56, 189, 248, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "var(--accent)",
                    }}
                  >
                    <FontAwesomeIcon icon={faTerminal} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>TryHackMe Security Profile</div>
                    <div className="font-mono text-muted" style={{ fontSize: "0.78rem" }}>
                      Handle: @XHunter101
                    </div>
                  </div>
                </div>

                <a
                  href={personalInfo.tryhackme}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-flex align-items-center gap-2"
                >
                  <img
                    src={personalInfo.tryhackmeBadge}
                    alt="TryHackMe Badge"
                    style={{ height: "36px", maxWidth: "100%", borderRadius: "4px" }}
                  />
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: "0.8rem", color: "var(--accent)" }} />
                </a>
              </div>
            </div>
          </div>

          {/* Right: Technical Profile Card & Core Areas */}
          <div className="col-lg-5">
            <div className="cyber-card p-4" style={{ background: "var(--bg-surface)" }}>
              {/* Profile Image & Metadata */}
              <div className="d-flex align-items-center gap-3 pb-3 mb-3" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                <img
                  src={personalInfo.avatar}
                  alt={personalInfo.name}
                  style={{
                    width: "68px",
                    height: "68px",
                    borderRadius: "14px",
                    objectFit: "cover",
                    border: "2px solid rgba(56, 189, 248, 0.4)",
                  }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: "1.1rem" }}>{personalInfo.name}</div>
                  <div className="font-mono" style={{ fontSize: "0.8rem", color: "var(--accent)" }}>
                    Security Analyst • MCA
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    Kolkata, India
                  </div>
                </div>
              </div>

              {/* Practical Focus List */}
              <div className="d-flex flex-column gap-3">
                <div className="font-mono text-muted text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.08em" }}>
                  Operational Core
                </div>

                {practicalHighlights.map((hl, idx) => (
                  <div key={idx} className="d-flex align-items-start gap-2">
                    <FontAwesomeIcon
                      icon={faShieldHalved}
                      style={{ color: "var(--accent)", marginTop: "4px", fontSize: "0.85rem", flexShrink: 0 }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "var(--text-primary)" }}>
                        {hl.title}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)", lineHeight: 1.5 }}>
                        {hl.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
